import type { ChoiceGroup, Meal, MealMenu, Regimen } from "@/data/meal-plan";
import type { ChoiceGroupSelections, DayPlan, MealSelections } from "@/types/meal-plan-state";

/** The menu chosen for a meal, if the meal uses menus and `menuId` names one of them. */
export function getMealMenu(meal: Meal, menuId: string | undefined): MealMenu | undefined {
  return meal.menus?.find((m) => m.id === menuId);
}

/**
 * The choice groups a day's selections for `meal` pair with: the chosen
 * menu's groups for menu meals (none until a menu is chosen), else
 * `meal.choices`. `menuId` is the day's `mealMenus[mealIndex]`.
 */
export function getMealChoices(meal: Meal, menuId: string | undefined): ChoiceGroup[] {
  if (!meal.menus) return meal.choices;
  return getMealMenu(meal, menuId)?.choices ?? [];
}

/** A menu group with a single, mandatory option: it starts selected. */
export function isFixedGroup(group: ChoiceGroup): boolean {
  return group.options.length === 1 && !group.optional;
}

/**
 * Fresh selections for a meal. In a menu, fixed groups come pre-selected at
 * `count = pick`; everything else starts empty.
 */
export function createMealSelections(meal: Meal, menuId?: string): MealSelections {
  return getMealChoices(meal, menuId).map((group) => ({
    selections:
      meal.menus && isFixedGroup(group)
        ? [{ optionId: group.options[0]!.id, count: group.pick }]
        : [],
    skipped: false,
  }));
}

export function createEmptyDaySelections(regimen: Regimen): MealSelections[] {
  return regimen.meals.map((meal) => createMealSelections(meal));
}

/**
 * Choose (or clear, with `null`) the menu of one meal of a day. The meal's
 * selections restart from the new menu's defaults.
 */
export function setDayMealMenu(
  dayPlan: DayPlan,
  meal: Meal,
  mealIndex: number,
  menuId: string | null,
): DayPlan {
  const { mealMenus: _, ...rest } = dayPlan;
  const mealMenus = { ...dayPlan.mealMenus };
  if (menuId) mealMenus[mealIndex] = menuId;
  else delete mealMenus[mealIndex];
  const fresh = createMealSelections(meal, menuId ?? undefined);
  return {
    ...rest,
    meals: dayPlan.meals?.map((m, i) => (i === mealIndex ? fresh : m)) ?? null,
    ...(Object.keys(mealMenus).length > 0 ? { mealMenus } : {}),
  };
}

/**
 * Clear some meals of a day: their selections restart empty (a menu meal
 * also forgets its menu) and their notes go. Other meals are untouched.
 */
export function clearMeals(dayPlan: DayPlan, regimen: Regimen, mealIndices: number[]): DayPlan {
  const { mealNotes: _notes, mealMenus: _menus, ...rest } = dayPlan;
  const notes = { ...dayPlan.mealNotes };
  const menus = { ...dayPlan.mealMenus };
  for (const mi of mealIndices) {
    delete notes[mi];
    delete menus[mi];
  }
  return {
    ...rest,
    meals:
      dayPlan.meals?.map((m, i) => (mealIndices.includes(i) ? createMealSelections(regimen.meals[i]!) : m)) ?? null,
    ...(Object.keys(notes).length > 0 ? { mealNotes: notes } : {}),
    ...(Object.keys(menus).length > 0 ? { mealMenus: menus } : {}),
  };
}

/**
 * Replace `mealIndices` of `target` with the source day's meals, notes and
 * chosen menus (meals absent from the source keep the target's selections).
 */
export function copyMealsFromDay(
  target: DayPlan,
  source: DayPlan,
  mealIndices: number[],
): DayPlan {
  const meals = target.meals
    ? target.meals.map((meal, i) =>
        mealIndices.includes(i) && source.meals?.[i] ? source.meals[i] : meal,
      )
    : source.meals;

  const notes = { ...(target.mealNotes ?? {}) };
  const menus = { ...(target.mealMenus ?? {}) };
  for (const mi of mealIndices) {
    if (source.mealNotes?.[mi]) notes[mi] = source.mealNotes[mi];
    else delete notes[mi];
    if (source.mealMenus?.[mi]) menus[mi] = source.mealMenus[mi];
    else delete menus[mi];
  }

  return {
    regimenId: target.regimenId,
    meals,
    ...(Object.keys(notes).length > 0 ? { mealNotes: notes } : {}),
    ...(Object.keys(menus).length > 0 ? { mealMenus: menus } : {}),
  };
}

export function isChoiceGroupComplete(
  group: ChoiceGroup,
  selections: ChoiceGroupSelections,
): boolean {
  if (group.optional && selections.skipped) return true;
  const total = selections.selections.reduce((sum, s) => sum + s.count, 0);
  return total === group.pick;
}

/** A menu meal is never complete before a menu is chosen. */
export function isMealComplete(
  meal: Meal,
  mealSel: MealSelections | undefined,
  menuId?: string,
): boolean {
  if (!mealSel) return false;
  if (meal.menus && !getMealMenu(meal, menuId)) return false;
  return getMealChoices(meal, menuId).every((group, i) => {
    const sel = mealSel[i];
    return !!sel && isChoiceGroupComplete(group, sel);
  });
}

export function isDayComplete(
  regimen: Regimen,
  dayMeals: MealSelections[],
  mealMenus?: DayPlan["mealMenus"],
): boolean {
  return regimen.meals.every((meal, i) =>
    isMealComplete(meal, dayMeals[i], mealMenus?.[i]),
  );
}
